import React from 'react'

import { DropdownStateReturn } from '../components/dropdown'
import { PopoverStateReturn } from '../components/toolbar/popover/hooks'
import { useWindowSize } from './use-window-size'

type Props = {
  maxHeightLimit: number
  hasViewportHeightLimit: boolean
  offsets: [number, number]
  state: DropdownStateReturn | PopoverStateReturn
}

const MAX_HEIGHT_PROPERTY = '--popover-max-height'
const POPOVER_HEIGHT_LIMIT_STYLE = {
  maxHeight: `calc(var(${MAX_HEIGHT_PROPERTY}) - env(safe-area-inset-bottom))`,
}

/**
 * Limit popover height to be always in viewport is `hasViewportHeightLimit` is true
 */
export function usePopoverMaxHeight({ maxHeightLimit, hasViewportHeightLimit, offsets, state }: Props) {
  const { innerHeight } = useWindowSize({ isWatching: state.visible || state.animating })
  const buttonElement = state.unstable_referenceRef.current

  React.useEffect(() => {
    if (!buttonElement || !state.visible || !hasViewportHeightLimit) return

    // calculate height
    const buttonRect = buttonElement.getBoundingClientRect()
    const [, offsetY] = offsets
    let elementMaxHeight = 'auto'
    if (state.placement.startsWith('bottom')) {
      let height = innerHeight - buttonRect.bottom - offsetY * 2
      // when used on pages where is scrollable content, buttonRect.bottom is sometimes negative
      // in that case we need to subtract window.scrollY
      if (buttonRect.bottom < 0) height -= window.scrollY
      elementMaxHeight = `${Math.min(height, maxHeightLimit)}px`
    } else {
      const height = buttonRect.top - offsetY * 2
      elementMaxHeight = `${Math.min(height, maxHeightLimit)}px`
    }

    // set height
    const popoverId = buttonElement.getAttribute('aria-controls')
    const popoverWrapper = popoverId ? document.getElementById(popoverId) : null
    popoverWrapper?.style.setProperty(MAX_HEIGHT_PROPERTY, elementMaxHeight)
  }, [buttonElement, innerHeight, maxHeightLimit, hasViewportHeightLimit, offsets, state.placement, state.visible])

  return hasViewportHeightLimit ? POPOVER_HEIGHT_LIMIT_STYLE : undefined
}
